import { esc, matchPlaces, nearestPlace, type SearchPlace } from '@puurweer/shared';

/**
 * Nav-island (Fase B + desktop + roadmap): verrijkt de v2-kop met een **locatie-overlay** — volledig
 * scherm (mobiel) / popover (desktop) — met live suggesties, **favoriete** en **recente** plaatsen,
 * geopend vanuit elke `[data-pw-loc-trigger]`.
 *
 * Volledig progressive enhancement (§progressive-enhancement): zónder JS gaat de chip naar `/zoek`.
 * Matcht client-side via de **gedeelde** `matchPlaces` — zelfde logica als de `/zoek`-SSR, dus geen
 * drift. Favorieten/recent blijven lokaal (localStorage, geen cookie/tracking). Importeert gericht
 * uit shared (sideEffects:false → geen zod in de bundel).
 */

// F6: alleen veilige slugs ([a-z0-9-]) → geen path-traversal/open-redirect via getamperde opslag.
const SLUG_RE = /^[a-z0-9-]+$/;
const PLACE_KEY = 'puurweer.plaats';
const RECENT_KEY = 'puurweer.recent';
const FAV_KEY = 'puurweer.favorieten';
const RECENT_MAX = 5;
const FAV_MAX = 12;

// Ster-icoon (favoriet-toggle) — client-only (de lijsten worden client-side gevuld). Outline; CSS
// vult 'm als favoriet. Eén bron hier i.p.v. de view-v2 server-iconen in de bundel te trekken.
const STAR =
  '<svg class="pw-star-ic" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3.4 L14.5 8.5 L20.2 9.3 L16.1 13.3 L17.1 18.9 L12 16.3 L6.9 18.9 L7.9 13.3 L3.8 9.3 L9.5 8.5 Z"/></svg>';

interface StoredPlace {
  slug: string;
  name: string;
}

/** Lees + valideer een plaatsenlijst (recent/favorieten) uit localStorage; kap op `max`. */
function readPlaces(key: string, max: number): StoredPlace[] {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(key);
  } catch {
    return []; // localStorage uit/onbeschikbaar
  }
  if (!stored) return [];
  try {
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    const out: StoredPlace[] = [];
    for (const item of parsed) {
      if (item && typeof item === 'object') {
        const slug = (item as Record<string, unknown>).slug;
        const name = (item as Record<string, unknown>).name;
        if (typeof slug === 'string' && SLUG_RE.test(slug) && typeof name === 'string') {
          out.push({ slug, name });
        }
      }
      if (out.length >= max) break;
    }
    return out;
  } catch {
    return [];
  }
}

function writePlaces(key: string, list: StoredPlace[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    // localStorage uit → niet erg
  }
}

const readRecent = (): StoredPlace[] => readPlaces(RECENT_KEY, RECENT_MAX);
const readFavorites = (): StoredPlace[] => readPlaces(FAV_KEY, FAV_MAX);

/** Voeg een favoriet toe/verwijder 'm (gededupliceerd, gekapt). Geeft de nieuwe staat terug. */
function toggleFavorite(place: StoredPlace): boolean {
  const cur = readFavorites();
  const exists = cur.some((f) => f.slug === place.slug);
  const next = exists
    ? cur.filter((f) => f.slug !== place.slug)
    : [{ slug: place.slug, name: place.name }, ...cur].slice(0, FAV_MAX);
  writePlaces(FAV_KEY, next);
  return !exists;
}

/** Onthoud een keuze: huidige plaats (PLACE_KEY) + bovenaan in de recente-lijst (gededupliceerd). */
function remember(place: StoredPlace): void {
  try {
    localStorage.setItem(PLACE_KEY, place.slug);
  } catch {
    return; // localStorage uit → navigeren kan alsnog
  }
  writePlaces(
    RECENT_KEY,
    [
      { slug: place.slug, name: place.name },
      ...readRecent().filter((r) => r.slug !== place.slug),
    ].slice(0, RECENT_MAX),
  );
}

/** Locatie-paneel: volledig scherm-overlay (mobiel) / popover (desktop) met suggesties + favorieten + recent. */
function initLocation(): void {
  const panelEl = document.getElementById('pw-loc-panel');
  const inputEl = document.getElementById('pw-loc-q');
  const listEl = document.getElementById('pw-loc-lb');
  const favWrap = document.getElementById('pw-loc-fav');
  const favList = document.getElementById('pw-loc-fav-list');
  const recentWrap = document.getElementById('pw-loc-recent');
  const recentList = document.getElementById('pw-loc-recent-list');
  const triggers = Array.from(document.querySelectorAll<HTMLElement>('[data-pw-loc-trigger]'));
  if (
    !(panelEl instanceof HTMLElement) ||
    !(inputEl instanceof HTMLInputElement) ||
    !(listEl instanceof HTMLElement) ||
    triggers.length === 0
  ) {
    return; // geen v2-kop → niets te verrijken (no-JS pad blijft /zoek)
  }
  // Aliassen met de gesmalde types, zodat de closures hieronder ze kennen (niet null).
  const panel = panelEl;
  const input = inputEl;
  const listbox = listEl;

  let index: SearchPlace[] | null = null;
  let options: SearchPlace[] = [];
  let active = -1;
  let lastTrigger: HTMLElement | null = null;

  async function loadIndex(): Promise<void> {
    if (index) return;
    try {
      const res = await fetch('/places.json');
      index = (await res.json()) as SearchPlace[];
    } catch {
      index = []; // stil falen; het form-submit naar /zoek blijft het fallback-pad
    }
    runQuery();
  }

  /** Eén rij: plaats-link + ster-toggle (favoriet). `fav` bepaalt de ster-staat (aria-pressed). */
  function rowHtml(p: StoredPlace, fav: boolean, current: string | undefined): string {
    const tag = p.slug === current ? ' <span class="pw-loc-cur">huidig</span>' : '';
    const label = `${p.name} ${fav ? 'uit favorieten halen' : 'als favoriet bewaren'}`;
    return `<li class="pw-loc-row"><a href="/${esc(p.slug)}" data-slug="${esc(p.slug)}" data-name="${esc(p.name)}">${esc(p.name)}${tag}</a><button type="button" class="pw-fav" data-fav-slug="${esc(p.slug)}" data-fav-name="${esc(p.name)}" aria-pressed="${fav ? 'true' : 'false'}" aria-label="${esc(label)}" title="${esc(label)}">${STAR}</button></li>`;
  }

  function fillGroup(
    wrap: Element | null,
    list: Element | null,
    items: StoredPlace[],
    fav: boolean,
  ): void {
    if (!(wrap instanceof HTMLElement) || !(list instanceof HTMLElement)) return;
    if (items.length === 0) {
      wrap.hidden = true;
      list.innerHTML = '';
      return;
    }
    const current = document.getElementById('place-name')?.dataset.slug;
    list.innerHTML = items.map((p) => rowHtml(p, fav, current)).join('');
    wrap.hidden = false;
  }

  /** Render favorieten + recent (recent zónder de favorieten, om dubbeling te voorkomen). */
  function renderLists(): void {
    const favs = readFavorites();
    const favSlugs = new Set(favs.map((f) => f.slug));
    fillGroup(favWrap, favList, favs, true);
    fillGroup(
      recentWrap,
      recentList,
      readRecent().filter((r) => !favSlugs.has(r.slug)),
      false,
    );
  }

  function isOpen(): boolean {
    return !panel.hidden;
  }

  function open(trigger: HTMLElement): void {
    lastTrigger = trigger;
    panel.hidden = false;
    panel.classList.add('pw-open');
    for (const t of triggers) t.setAttribute('aria-expanded', 'true');
    renderLists();
    void loadIndex();
    // Focus het zoekveld zodra het paneel zichtbaar is (volledig scherm-overlay op mobiel).
    input.focus();
  }

  function close(): void {
    if (!isOpen()) return;
    panel.hidden = true;
    panel.classList.remove('pw-open');
    for (const t of triggers) t.setAttribute('aria-expanded', 'false');
    closeList();
    lastTrigger?.focus();
  }

  function closeList(): void {
    listbox.innerHTML = '';
    listbox.classList.remove('open');
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    options = [];
    active = -1;
  }

  function renderList(): void {
    listbox.innerHTML =
      options.length === 0
        ? '<li class="pw-loc-empty">Geen plaats gevonden</li>'
        : options
            .map(
              (p, i) =>
                `<li role="option" id="pwlo${i}" aria-selected="${i === active}" data-i="${i}">${esc(p.name)} <small>${esc(p.province)}</small></li>`,
            )
            .join('');
    listbox.classList.add('open');
    input.setAttribute('aria-expanded', 'true');
  }

  function setActive(i: number): void {
    active = i;
    for (const [j, li] of listbox.querySelectorAll('[role=option]').entries()) {
      li.setAttribute('aria-selected', String(j === active));
    }
    if (active >= 0) input.setAttribute('aria-activedescendant', `pwlo${active}`);
    else input.removeAttribute('aria-activedescendant');
  }

  function runQuery(): void {
    const q = input.value.trim();
    if (!q) {
      closeList();
      return;
    }
    if (!index) return; // nog aan het laden — render zodra de index binnen is
    options = matchPlaces(index, q, 8);
    active = -1;
    renderList();
  }

  function choose(place: SearchPlace): void {
    remember({ slug: place.slug, name: place.name });
    location.assign(`/${place.slug}`);
  }

  // ── Triggers: chip + "Locatie wijzigen" openen/sluiten het paneel (no-JS: gewone link naar /zoek) ──
  for (const t of triggers) {
    t.setAttribute('aria-haspopup', 'dialog');
    t.setAttribute('aria-expanded', 'false');
    t.setAttribute('aria-controls', 'pw-loc-panel');
    t.addEventListener('click', (event) => {
      event.preventDefault();
      if (isOpen()) close();
      else open(t);
    });
  }

  input.addEventListener('input', runQuery);

  input.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (!listbox.classList.contains('open') || options.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((active + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((active - 1 + options.length) % options.length);
    } else if (event.key === 'Enter') {
      const chosen = options[active];
      if (chosen) {
        event.preventDefault();
        choose(chosen);
      }
      // geen selectie → laat de form submitten naar /zoek (no-JS resultatenpagina)
    }
  });

  listbox.addEventListener('click', (event) => {
    const li = event.target instanceof Element ? event.target.closest('[data-i]') : null;
    if (!li) return;
    const chosen = options[Number(li.getAttribute('data-i'))];
    if (chosen) choose(chosen);
  });

  // Favorieten + recent (gedelegeerd): ster togglet favoriet (geen navigatie), link kiest de plaats.
  panel.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const favBtn = target.closest('.pw-fav');
    if (favBtn) {
      event.preventDefault();
      const slug = favBtn.getAttribute('data-fav-slug');
      const name = favBtn.getAttribute('data-fav-name');
      if (slug && SLUG_RE.test(slug) && name) {
        toggleFavorite({ slug, name });
        renderLists();
      }
      return;
    }
    const a = target.closest('.pw-loc-list a[data-slug]');
    if (a instanceof HTMLAnchorElement) {
      const slug = a.getAttribute('data-slug');
      const name = a.getAttribute('data-name');
      if (slug && SLUG_RE.test(slug) && name) {
        event.preventDefault();
        choose({ slug, name, province: '', aliases: [] });
      }
    }
  });

  // Sluiten: Escape (overal in het paneel), sluitknop, scrim, en klik buiten.
  panel.addEventListener('keydown', (event) => {
    if ((event as KeyboardEvent).key === 'Escape') close();
  });
  panel.querySelector('.pw-loc-close')?.addEventListener('click', close);
  document.addEventListener('click', (event) => {
    if (!isOpen()) return;
    const target = event.target instanceof Node ? event.target : null;
    if (!target) return;
    if (panel.contains(target)) return;
    if (triggers.some((t) => t === target || t.contains(target))) return;
    close();
  });

  // "Gebruik mijn locatie" (geolocatie): alleen tonen als de browser het kan. De coördinaten worden
  // CLIENT-SIDE naar de dichtstbijzijnde plaats vertaald (nearestPlace) — de locatie verlaat het
  // toestel niet (privacy). No-JS / niet-ondersteund → knop blijft verborgen (chip → /zoek).
  const geoBtn = document.getElementById('pw-geo');
  const geoHint = document.getElementById('pw-geo-hint');
  const geoMsg = document.getElementById('pw-geo-msg');
  if (geoBtn instanceof HTMLButtonElement && 'geolocation' in navigator) {
    geoBtn.hidden = false;
    if (geoHint instanceof HTMLElement) geoHint.hidden = false;
    const say = (text: string): void => {
      if (geoMsg instanceof HTMLElement) {
        geoMsg.textContent = text;
        geoMsg.hidden = false;
      }
    };
    geoBtn.addEventListener('click', () => {
      void (async () => {
        geoBtn.disabled = true;
        // Zonder https geeft de browser géén prompt maar direct een weigering — leg dat uit.
        if (typeof isSecureContext !== 'undefined' && !isSecureContext) {
          say('Locatie werkt alleen via een beveiligde (https-)verbinding.');
          geoBtn.disabled = false;
          return;
        }
        // Pre-flight: staat de site op "blokkeren", dan komt er géén prompt en faalt de
        // aanvraag direct — vertel dan wáár het aan te zetten is i.p.v. dood te lopen.
        try {
          const st = await navigator.permissions?.query({ name: 'geolocation' });
          if (st?.state === 'denied') {
            say(
              'Je browser blokkeert locatie voor deze site. Sta het toe via het slotje naast het adres (of je browserinstellingen) en probeer het opnieuw.',
            );
            geoBtn.disabled = false;
            return;
          }
        } catch {
          // Permissions API niet beschikbaar (oudere Safari) → gewoon proberen
        }
        say('Locatie bepalen…');
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            void (async () => {
              try {
                // Vers ophalen (cache omzeilen): terugkerende bezoekers kunnen nog een oude
                // /places.json zónder lat/lon gecachet hebben — dan zou nearest-place niets vinden.
                const res = await fetch('/places.json', { cache: 'reload' });
                const places = (await res.json()) as SearchPlace[];
                const near = nearestPlace(places, pos.coords.latitude, pos.coords.longitude);
                if (near) {
                  remember({ slug: near.slug, name: near.name });
                  location.assign(`/${near.slug}`);
                  return;
                }
              } catch {
                // val door naar de melding hieronder
              }
              say('Kon je plaats niet bepalen. Zoek hierboven een plaats.');
              geoBtn.disabled = false;
            })();
          },
          (err) => {
            say(
              err.code === err.PERMISSION_DENIED
                ? 'Geen toestemming gegeven. Sta locatie toe via het slotje naast het adres en probeer het opnieuw.'
                : 'Locatie niet beschikbaar — staat locatie aan in je systeeminstellingen? Zoek anders hierboven een plaats.',
            );
            geoBtn.disabled = false;
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 },
        );
      })();
    });
  }
}

// Kruis-icoon voor de waarschuwingsbanner — client-only (zelfde reden als STAR): de sluitknop
// bestaat alleen mét JS, dus het icoon hoort niet in de server-HTML.
const ALERT_CLOSE =
  '<svg class="pw-close-ico" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>';

// Snooze: na wegklikken blijft dezélfde waarschuwing dit aantal ms verborgen. Bewust kort (6 uur) —
// een weerwaarschuwing mag je niet permanent kwijtraken, dus na de snooze (of zodra de waarschuwing
// verandert/escaleert → andere sleutel) verschijnt hij weer.
const ALERT_SNOOZE_KEY = 'puurweer.alert-snooze';
const ALERT_SNOOZE_MS = 6 * 60 * 60 * 1000;

interface AlertSnooze {
  key: string;
  expires: number;
}

/** Lees de bewaarde snooze (sleutel + verloopmoment); ongeldig/uit → null. */
function readSnooze(): AlertSnooze | null {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(ALERT_SNOOZE_KEY);
  } catch {
    return null; // localStorage uit/onbeschikbaar
  }
  if (!stored) return null;
  try {
    const parsed: unknown = JSON.parse(stored);
    if (parsed && typeof parsed === 'object') {
      const key = (parsed as Record<string, unknown>).key;
      const expires = (parsed as Record<string, unknown>).expires;
      if (typeof key === 'string' && typeof expires === 'number') return { key, expires };
    }
  } catch {
    // val door
  }
  return null;
}

/**
 * Waarschuwingsbanner wegklikbaar: de island **injecteert** het kruisje (zonder JS zou het een dode
 * knop zijn, dus rendert de server 'm niet). Wegklikken **snoozt** dezelfde waarschuwing kort: de
 * sleutel (`data-pw-alert-key` = severity + verschijnsel + geldig-tot) + verloopmoment gaan in
 * localStorage, zodat exact dezelfde waarschuwing niet bij elke herlaad terugkomt. Is er bij init een
 * geldige snooze voor déze sleutel, dan blijft de banner verborgen; is de snooze verlopen of wijst hij
 * naar een andere (gewijzigde/zwaardere) waarschuwing, dan toont de banner gewoon. Zonder JS blijft de
 * waarschuwing altijd staan.
 */
function initAlert(): void {
  const alert = document.querySelector('.pw-alert');
  if (!(alert instanceof HTMLElement) || alert.hidden) return;
  const key = alert.dataset.pwAlertKey ?? '';

  // Actieve snooze voor déze waarschuwing → meteen verbergen (geen kruisje nodig). Andere sleutel of
  // verlopen → de bewaarde snooze is achterhaald: opruimen en de banner normaal tonen.
  const snooze = readSnooze();
  if (snooze && snooze.key === key && Date.now() < snooze.expires) {
    alert.hidden = true;
    return;
  }
  if (snooze) {
    try {
      localStorage.removeItem(ALERT_SNOOZE_KEY);
    } catch {
      // localStorage uit → niet erg
    }
  }

  const top = alert.querySelector('.pw-alert-top');
  if (!top || top.querySelector('.pw-alert-close')) return;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'pw-alert-close';
  btn.setAttribute('aria-label', 'Waarschuwing sluiten');
  btn.innerHTML = ALERT_CLOSE;
  btn.addEventListener('click', () => {
    alert.hidden = true;
    try {
      localStorage.setItem(
        ALERT_SNOOZE_KEY,
        JSON.stringify({ key, expires: Date.now() + ALERT_SNOOZE_MS }),
      );
    } catch {
      // localStorage uit → dan is het wegklikken alleen voor deze weergave (niet erg)
    }
  });
  top.append(btn);
}

/**
 * Merk-klik = rustig naar boven (geen herlaad). Op de plaatspagina wijst het merk (kop + footer) naar
 * de eigen plaats-URL (`/<slug>`), zodat een logo-klik niet via `/` (= Rotterdam) omloopt en door de
 * home-restore terugkaatst — dat gaf een zichtbare Rotterdam-flits. Sta je al op die plaats, dan is
 * navigeren een nutteloze herlaad: onderschep een gewone klik en scroll naar boven (respecteert
 * reduced-motion). Modifier-/middenkliks (nieuw tabblad) laten we met rust; zónder JS blijft het een
 * gewone link naar de eigen plaats (herlaadt dezelfde pagina, nog steeds geen flits).
 */
function initBrandHome(): void {
  const here = location.pathname;
  for (const a of document.querySelectorAll<HTMLAnchorElement>('a.pw-brand, a.pw-footer-brand')) {
    if (a.pathname !== here) continue; // wijst elders (bv. de homepage → /rotterdam) → laat lopen
    a.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return; // nieuw tabblad enz.
      event.preventDefault();
      const reduce =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
  }
}

function init(): void {
  initLocation();
  initAlert();
  initBrandHome();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

export {
  init,
  initAlert,
  initBrandHome,
  initLocation,
  readFavorites,
  readRecent,
  remember,
  toggleFavorite,
};
